import {
	createFileRoute,
	Link,
	useNavigate,
	useRouter,
} from "@tanstack/react-router";
import { type FormEvent, useId, useState, useTransition } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { signInUser } from "$/modules/auth/lib/auth";
import { AuthLayout } from "$/modules/auth/ui/auth-layout";
import { OAuthButtons } from "$/modules/auth/ui/oauth-buttons";
import { PasswordInput } from "$/modules/auth/ui/password-input";
import { Button } from "$/modules/shared/ui/button";
import { Field, FieldLabel } from "$/modules/shared/ui/field";
import { Input } from "$/modules/shared/ui/input";
import { Spinner } from "$/modules/shared/ui/spinner";

export const Route = createFileRoute("/$locale/signin/")({
	component: SignInPage,
});

function SignInPage() {
	const fieldId = useId();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const t = useTranslations();
	const { locale } = Route.useParams();
	const redirect =
		new URLSearchParams(window.location.search).get("redirect") ?? undefined;
	const [isPending, startTransition] = useTransition();
	const navigate = useNavigate();
	const router = useRouter();

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		startTransition(async () => {
			const response = await signInUser({ email, password });
			if (!response.success) {
				toast.error(response.message, {});
				return;
			}

			if (redirect?.startsWith(`/${locale}/exam/`)) {
				window.location.assign(redirect);
				return;
			}
			router.clearCache();
			await navigate({
				to: "/$locale/$userId/projects",
				params: { locale, userId: response.data },
				replace: true,
			});
		});
	};

	return (
		<AuthLayout
			locale={locale}
			title={t("signIn")}
			subtitle={t("signInDescription")}
			footer={
				<span>
					{t("noAccount")}{" "}
					<a
						href={`/${locale}/signup${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`}
						className="font-semibold text-primary hover:underline"
					>
						{t("createAccount")}
					</a>
				</span>
			}
		>
			<form onSubmit={handleSubmit} className="space-y-4">
				<Field>
					<FieldLabel htmlFor={`${fieldId}-100`}>{t("email")}</FieldLabel>
					<Input
						id={`${fieldId}-100`}
						autoComplete="email"
						type="email"
						placeholder={t("emailPlaceholder")}
						value={email}
						required
						onChange={(event) => setEmail(event.currentTarget.value)}
					/>
				</Field>

				<PasswordInput
					label={t("password")}
					placeholder={t("passwordPlaceholder")}
					value={password}
					onValueChange={setPassword}
					isRequired
				/>

				<div className="flex justify-end">
					<Link
						to="/$locale/forgot-password"
						params={{ locale }}
						className="text-sm text-foreground-500 hover:text-foreground"
					>
						{t("forgotPassword")}
					</Link>
				</div>

				<Button
					type="submit"
					size="lg"
					className="w-full font-semibold"
					disabled={isPending}
					variant="default"
				>
					{isPending && <Spinner data-icon="inline-start" />}
					{t("signIn")}
				</Button>
			</form>

			<OAuthButtons locale={locale} returnTo={redirect} />
		</AuthLayout>
	);
}
